import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Upload, Trash2, Loader2, Image as ImageIcon, Copy, Check, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminImages = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [images, setImages] = useState([]);
  const [copiedId, setCopiedId] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/images`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setImages(response.data);
    } catch (error) {
      toast.error('Görseller yüklenemedi');
    }
    setLoading(false);
  };

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);

      try {
        const response = await axios.post(`${API_URL}/api/images/upload`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        toast.success(`${file.name} yüklendi (${response.data.savings} optimize edildi)`);
      } catch (error) {
        toast.error(`${file.name} yüklenemedi: ${error.response?.data?.detail || 'Hata'}`);
      }
    }

    setUploading(false);
    fetchImages();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (imageId) => {
    if (!window.confirm('Bu görseli silmek istediğinize emin misiniz?')) return;

    try {
      await axios.delete(`${API_URL}/api/images/${imageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Görsel silindi');
      fetchImages();
    } catch (error) {
      toast.error('Görsel silinemedi');
    }
  };

  const copyImageUrl = (imageId) => {
    const url = `${API_URL}/api/images/${imageId}`;
    navigator.clipboard.writeText(url);
    setCopiedId(imageId);
    toast.success('URL kopyalandı');
    setTimeout(() => setCopiedId(null), 2000);
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#002FA7]" />
      </div>
    );
  }

  return (
    <div data-testid="admin-images">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Görsel Yönetimi</h1>
          <p className="text-slate-600">Görselleri yükleyin ve yönetin. Otomatik optimizasyon yapılır.</p>
        </div>
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleUpload}
            className="hidden"
            id="image-upload"
            data-testid="image-upload-input"
          />
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="bg-[#002FA7] hover:bg-[#001d6e]"
            data-testid="upload-image-btn"
          >
            {uploading ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Upload className="w-4 h-4 mr-2" />
            )}
            Görsel Yükle
          </Button>
        </div>
      </div>

      {/* Info Card */}
      <Card className="mb-6 bg-blue-50 border-blue-200">
        <CardContent className="p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium">Otomatik Görsel Optimizasyonu</p>
            <p>Yüklenen görseller otomatik olarak web için optimize edilir (maks. 1200x800px, JPEG formatı, %85 kalite). Büyük dosyalar küçültülür ve sıkıştırılır.</p>
          </div>
        </CardContent>
      </Card>

      {images.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <ImageIcon className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600">Henüz görsel yüklenmemiş.</p>
            <p className="text-sm text-slate-400 mt-1">Yukarıdaki butona tıklayarak görsel yükleyebilirsiniz.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {images.map((image, index) => (
            <Card key={image.id} className="overflow-hidden" data-testid={`image-item-${index}`}>
              <div className="aspect-video bg-slate-100 relative">
                <img
                  src={`data:${image.content_type};base64,${image.data}`}
                  alt={image.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <CardContent className="p-3">
                <p className="text-sm font-medium text-slate-900 truncate" title={image.name}>
                  {image.name}
                </p>
                <p className="text-xs text-slate-500">
                  {formatBytes(image.size)}
                  {image.original_size && image.original_size !== image.size && (
                    <span className="text-green-600 ml-1">
                      ({Math.round((1 - image.size / image.original_size) * 100)}% küçültüldü)
                    </span>
                  )}
                </p>
                <div className="flex gap-2 mt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => copyImageUrl(image.id)}
                  >
                    {copiedId === image.id ? (
                      <Check className="w-3 h-3" />
                    ) : (
                      <Copy className="w-3 h-3" />
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(image.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminImages;
